/**
 * Common module
 *
 * Module index file
 *
 * This file is used by theme to provide default functionality
 * to the module and to register all components.
 *
 * This file has to export setup function that will be called
 * automatically by import in the theme app.ts file.
 */

import { App } from 'vue';
import { Link } from '@inertiajs/vue3';

/*
  defining the <load-file> Web Component
  License: https://unlicense.org/
*/

// Cache for loaded SVG files - stores Promises to deduplicate concurrent requests
const svgCache = new Map<string, Promise<string>>();

customElements.define(
    'load-file',
    class extends HTMLElement {
        // declare default connectedCallback as async so await can be used
        async connectedCallback(
            // call connectedCallback with parameter to *replace* SVG (of <load-file> persists)
            src = this.getAttribute('src'),
            // attach a shadowRoot if none exists (prevents displaying error when moving Nodes)
            // declare as parameter to save 4 Bytes: 'let '
            shadowRoot = this.shadowRoot || this.attachShadow({ mode: 'open' }),
        ) {
            // Use cache or create new request. Failed fetches (navigation
            // abort, network drop) must not poison the cache or surface as
            // unhandled rejections — drop the entry so a later render retries.
            if (!svgCache.has(src)) {
                svgCache.set(
                    src,
                    fetch(src)
                        .then((r) => {
                            if (!r.ok) {
                                throw new Error(`Failed to load ${src}`);
                            }

                            return r.text();
                        })
                        .catch(() => {
                            svgCache.delete(src);

                            return '';
                        }),
                );
            }

            // load SVG file from src="" async, parse to text, add to shadowRoot.innerHTML
            // Note: innerHTML is safe here as SVG comes from own server (/svg/ endpoint), not user input
            shadowRoot.innerHTML = await svgCache.get(src);

            // append optional <tag [shadowRoot]> Elements from <load-svg> innerHTML/lightDOM after parsed <svg>
            shadowRoot.append(...this.querySelectorAll('[shadowRoot]'));

            // if "replaceWith" attribute exists
            // then replace <load-svg> with loaded content <load-svg>
            // childNodes instead of children to include #textNodes also
            if (this.hasAttribute('replaceWith')) {
                this.replaceWith(...shadowRoot.childNodes);
            }
        }
    },
);

import Boolean from '@src/Common/Components/Boolean.vue';
import IconBrand from '@src/Common/Components/IconBrand.vue';
import Button from '@src/Common/Components/Button.vue';
import ButtonCopyToClipboard from '@src/Common/Components/ButtonCopyToClipboard.vue';
import Confirmation from '@src/Common/Components/Confirmation.vue';
import CreditCard from '@src/Common/Components/Fields/Masked/CreditCard.vue';
import ExportButton from '@src/Common/Components/ExportButton.vue';
import Field from '@src/Common/Components/Field.vue';
import Gender from '@src/Common/Components/Gender.vue';
import Icon from '@src/Common/Components/Icon.vue';
import IllustratedMessage from '@src/Common/Components/IllustratedMessage.vue';
import InputGroup from '@src/Common/Components/InputGroup.vue';
import Label from '@src/Common/Components/Label.vue';
import Loading from '@src/Common/Components/Loading.vue';
import Modal from '@src/Common/Components/Modal.vue';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import PanelActions from '@src/Common/Components/PanelActions.vue';
import Popover from '@src/Common/Components/Popover.vue';
import QrScan from '@src/Common/Components/QrScan.vue';
import Tabs from '@src/Common/Components/Tabs.vue';
import Tag from '@src/Common/Components/Tag.vue';
import Sortable from '@src/Common/Components/Sortable.vue';

import UiContextMenu from '@src/Common/Components/UI/ContextMenu/ContextMenu.vue';
import UiContextMenuItem from '@src/Common/Components/UI/ContextMenu/ContextMenuItem.vue';
import UiFloatDropdown from '@src/Common/Components/UI/Float/Dropdown.vue';
import UiPopover from '@src/Common/Components/UI/Popover.vue';
import UiDrawerPanel from '@src/Common/Components/UI/UiDrawerPanel.vue';
import UiHeaderButton from '@src/Common/Components/UI/UiHeaderButton.vue';
import LayoutSlot from '@src/Common/Components/UI/LayoutSlot.vue';
import UiPopoverHeader from '@src/Common/Components/UI/PopoverHeader.vue';
import UiPopoverHeadline from '@src/Common/Components/UI/PopoverHeadline.vue';
import UiPopoverLink from '@src/Common/Components/UI/PopoverLink.vue';
import UiPopoverSection from '@src/Common/Components/UI/PopoverSection.vue';
import UiPopoverSegment from '@src/Common/Components/UI/PopoverSegment.vue';
import UiPopoverSubtitle from '@src/Common/Components/UI/PopoverSubtitle.vue';
import UiScrollable from '@src/Common/Components/UI/Scrollable.vue';
import UiCard from '@src/Common/Components/UI/Card.vue';
import UiCardEmpty from '@src/Common/Components/UI/CardEmpty.vue';
import UiSectionTitle from '@src/Common/Components/UI/SectionTitle.vue';
import UiDataItem from '@src/Common/Components/UI/DataItem.vue';
import UiInfoTable from '@src/Common/Components/UI/InfoTable.vue';
import UiCardGrid from '@src/Common/Components/UI/CardGrid.vue';
import UiCardList from '@src/Common/Components/UI/CardList.vue';
import UiCardListHeader from '@src/Common/Components/UI/CardListHeader.vue';
import UiListCard from '@src/Common/Components/UI/ListCard.vue';
import UiBadge from '@src/Common/Components/UI/Badge.vue';
import UiBadgeList from '@src/Common/Components/UI/BadgeList.vue';
import UiKPI from '@src/Common/Components/UI/KPI.vue';
import UiValidationErrors from '@src/Common/Components/UI/ValidationErrors.vue';
import UiSelectableCard from '@src/Common/Components/UI/SelectableCard.vue';
import UiModalHeader from '@src/Common/Components/UI/ModalHeader.vue';
import KbdShortcut from '@src/Common/Components/KbdShortcut.vue';
import ShortcutLegend from '@src/Common/Components/ShortcutLegend.vue';
import TabsProgress from '@src/Common/Components/TabsProgress.vue';

import { vTooltip } from '@src/Common/Directives/tooltip';

export const setup = (app: App<Element>): void => {
    app.component('InertiaLink', Link);

    app.component('Boolean', Boolean);
    app.component('IconBrand', IconBrand);
    app.component('Button', Button);
    app.component('ButtonCopyToClipboard', ButtonCopyToClipboard);
    app.component('Confirmation', Confirmation);
    app.component('CreditCard', CreditCard);
    app.component('ExportButton', ExportButton);
    app.component('Field', Field);
    app.component('Gender', Gender);
    app.component('Icon', Icon);
    app.component('IllustratedMessage', IllustratedMessage);
    app.component('InputGroup', InputGroup);
    app.component('Label', Label);
    app.component('Loading', Loading);
    app.component('Modal', Modal);
    app.component('MountedTeleport', MountedTeleport);
    app.component('PanelActions', PanelActions);
    app.component('Popover', Popover);
    app.component('QrScan', QrScan);
    app.component('Sortable', Sortable);
    app.component('Tabs', Tabs);
    app.component('Tag', Tag);

    app.component('UiContextMenu', UiContextMenu);
    app.component('UiContextMenuItem', UiContextMenuItem);
    app.component('UiFloatDropdown', UiFloatDropdown);
    app.component('UiPopover', UiPopover);
    app.component('UiDrawerPanel', UiDrawerPanel);
    app.component('UiHeaderButton', UiHeaderButton);
    app.component('LayoutSlot', LayoutSlot);
    app.component('UiPopoverHeader', UiPopoverHeader);
    app.component('UiPopoverHeadline', UiPopoverHeadline);
    app.component('UiPopoverLink', UiPopoverLink);
    app.component('UiPopoverSection', UiPopoverSection);
    app.component('UiPopoverSegment', UiPopoverSegment);
    app.component('UiPopoverSubtitle', UiPopoverSubtitle);
    app.component('UiScrollable', UiScrollable);
    app.component('UiCard', UiCard);
    app.component('UiCardEmpty', UiCardEmpty);
    app.component('UiSectionTitle', UiSectionTitle);
    app.component('UiDataItem', UiDataItem);
    app.component('UiInfoTable', UiInfoTable);
    app.component('UiCardGrid', UiCardGrid);
    app.component('UiCardList', UiCardList);
    app.component('UiCardListHeader', UiCardListHeader);
    app.component('UiListCard', UiListCard);
    app.component('UiBadge', UiBadge);
    app.component('UiBadgeList', UiBadgeList);
    app.component('UiKPI', UiKPI);
    app.component('UiValidationErrors', UiValidationErrors);
    app.component('UiSelectableCard', UiSelectableCard);
    app.component('UiModalHeader', UiModalHeader);
    app.component('KbdShortcut', KbdShortcut);
    app.component('ShortcutLegend', ShortcutLegend);
    app.component('TabsProgress', TabsProgress);

    app.directive('tooltip', vTooltip);
};
